/**
 * Prompt builder — assembles the OpenAI/Claude/Gemini request. OpenAI is the
 * DESIGNER: the detailed Designer System Prompt teaches the craft, the user
 * message supplies the brief + resolved industry branding + the Design JSON
 * schema + the N-distinct-concepts instruction. No deterministic layout logic
 * is applied here; the model decides everything.
 *
 * Built on the frontend (where the schema, taxonomy, and branding live) and
 * forwarded verbatim by the backend proxy, so schema and validator never
 * drift into a second codebase.
 */

import { DESIGNER_SYSTEM_PROMPT } from './designerSystemPrompt';
import { getIndustryBranding } from './industryBranding';

export const DESIGN_MODEL = 'gpt-4o';

const SCHEMA_SPEC = `
Return a JSON object: { "templates": [ <design>, ... ] } with EXACTLY {count} designs.
Each <design> (all coordinates in pixels, origin top-left):
{
  "concept": "short label naming this concept's idea",
  "canvas": { "width": <int>, "height": <int>, "background": "#hex" },
  "theme": { "name": "string", "mood": "string" },
  "typography": { "heading": { "family": "string", "weight": <int> }, "body": { "family": "string", "weight": <int> } },
  "colors": { "primary": "#hex", "secondary": "#hex", "accent": "#hex", "background": "#hex", "text": "#hex" },
  "sections": [ { "id": "string", "role": "hero|headline|offer|cta|body|footer|...", "x": <int>, "y": <int>, "width": <int>, "height": <int> } ],
  "objects": [
    {
      "id": "unique-string", "type": "text|shape|button|badge|icon|image",
      "x": <int>, "y": <int>, "width": <int>, "height": <int>, "rotation": <int>, "z": <int>,
      "group": "optional group id", "editable": true,
      "text": "string", "fontSize": <int>, "fontFamily": "string", "fontWeight": <int>, "color": "#hex", "align": "left|center|right", "lineHeight": <number>, "letterSpacing": <number>,
      "shape": "rect|roundedRect|circle|triangle|diamond|star", "fill": "#hex", "cornerRadius": <int>, "opacity": <number>,
      "icon": "name", "iconColor": "#hex",
      "query": "stock image search terms", "fit": "cover|contain",
      "shadow": { "offsetX": <int>, "offsetY": <int>, "blur": <int>, "color": "rgba()" },
      "gradient": { "colors": ["#hex","#hex"], "angle": <int> },
      "stroke": { "color": "#hex", "width": <int> },
      "blendMode": "normal|multiply|screen|overlay"
    }
  ]
}`;

/**
 * Build the request payload for `count` distinct concepts.
 * Returns { model, count, messages, response_format }.
 */
export const buildDesignPrompt = (brief, count = 1) => {
  const n = Math.max(1, Math.min(10, Math.round(count) || 1));
  const branding = getIndustryBranding(brief);

  const user = [
    `Design ${n} premium, COMPLETELY DIFFERENT, editable ${brief.documentType || 'template'} concept${n > 1 ? 's' : ''} for a marketplace.`,
    `Canvas: ${brief.canvas.width}x${brief.canvas.height}px (${brief.orientation}).`,
    '',
    'BRAND DIRECTION:',
    branding,
    '',
    'BRIEF:',
    JSON.stringify(brief, null, 2),
    '',
    n > 1
      ? `Make the ${n} concepts feel like ${n} different senior designers each made one — no shared composition, no mirrored layouts, no recolored copies.`
      : 'Deliver one polished, art-directed concept.',
    '',
    SCHEMA_SPEC.replace('{count}', String(n)),
  ].join('\n');

  return {
    model: DESIGN_MODEL,
    count: n,
    response_format: { type: 'json_object' },
    messages: [
      { role: 'system', content: DESIGNER_SYSTEM_PROMPT },
      { role: 'user', content: user },
    ],
  };
};

export { DESIGNER_SYSTEM_PROMPT, SCHEMA_SPEC };
